import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Bed, Bath, Ruler, ArrowUpRight, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import SaveHeart from "./SaveHeart";

const fmt = (n) =>
  n === undefined || n === null ? "—" : `$${Number(n).toLocaleString()}`;

/**
 * FeaturedMlsCarousel — pulls the 6 newest luxury MLS listings ($750K+)
 * from the live HAR/Trestle feed and displays them as a horizontal scroll carousel.
 */
export default function FeaturedMlsCarousel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const scrollerRef = useRef(null);

  const fetchListings = async ({ manual = false } = {}) => {
    if (manual) setRefreshing(true);
    try {
      const { data } = await api.get("/mls/listings", {
        params: { min_price: 750000, status: "Active", limit: 6 },
      });
      setItems(data.items || []);
      setLastUpdated(new Date());
    } catch {
      /* silent */
    } finally {
      setLoading(false);
      if (manual) setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchListings();
    // Auto-refresh every 5 minutes so freshly-synced luxury listings surface without a reload
    const interval = setInterval(fetchListings, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-carousel-card]");
    const cardW = card ? card.clientWidth + 24 : 400;
    el.scrollBy({ left: dir * cardW, behavior: "smooth" });
  };

  if (!loading && items.length === 0) return null;

  return (
    <section
      className="bg-[#0A0A0A] text-white relative overflow-hidden"
      data-testid="home-featured-mls-carousel"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#C9A961]" />
              <div className="eyebrow text-white/80 flex items-center gap-2 flex-wrap">
                Live · HAR MLS · $750K+
                <span className="inline-flex items-center gap-1 text-white/60 normal-case tracking-normal text-[10px]">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  {lastUpdated
                    ? `Updated ${lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                    : "Refreshing every 5 min"}
                </span>
                <button
                  onClick={() => fetchListings({ manual: true })}
                  disabled={refreshing}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-white/70 hover:text-[#E63946] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="mls-carousel-refresh-now"
                  title="Refresh now — force an immediate re-fetch"
                >
                  <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} strokeWidth={2} />
                  {refreshing ? "Refreshing…" : "Refresh now"}
                </button>
              </div>
            </div>
            <h2 className="font-display text-4xl lg:text-6xl leading-[0.95] tracking-tight text-white font-light max-w-2xl">
              Fresh luxury listings, <span className="serif-italic text-[#E63946]">just added.</span>
            </h2>
            <p className="text-white/60 mt-5 max-w-lg font-light">
              Pulled straight from the Houston MLS feed, refreshed nightly. The newest six homes above $750K, before they hit the wider market.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => scrollBy(-1)}
              className="w-11 h-11 rounded-full border border-white/25 hover:border-[#E63946] hover:bg-[#E63946] text-white flex items-center justify-center transition-colors"
              aria-label="Previous"
              data-testid="mls-carousel-prev"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <button
              onClick={() => scrollBy(1)}
              className="w-11 h-11 rounded-full border border-white/25 hover:border-[#E63946] hover:bg-[#E63946] text-white flex items-center justify-center transition-colors"
              aria-label="Next"
              data-testid="mls-carousel-next"
            >
              <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 lg:-mx-12 px-6 lg:px-12 scroll-hide"
          data-testid="mls-carousel-scroller"
        >
          {loading &&
            Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="shrink-0 w-[85%] sm:w-[420px] snap-start bg-white/5 border border-white/10 rounded-2xl overflow-hidden animate-pulse"
              >
                <div className="aspect-[4/3] bg-white/5" />
                <div className="p-6 space-y-3">
                  <div className="h-6 w-32 bg-white/10 rounded" />
                  <div className="h-4 w-full bg-white/10 rounded" />
                  <div className="h-4 w-2/3 bg-white/10 rounded" />
                </div>
              </div>
            ))}

          {items.map((l) => (
            <Link
              key={l.listing_key}
              to={`/mls-listings/${l.listing_key}`}
              data-carousel-card
              data-testid={`mls-carousel-card-${l.listing_key}`}
              className="shrink-0 w-[85%] sm:w-[420px] snap-start group bg-white text-[#1A1A1A] rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-500"
            >
              <div className="relative aspect-[4/3] bg-stone-100 overflow-hidden">
                {l.photos?.[0] ? (
                  <img
                    src={l.photos[0]}
                    alt={l.address}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms] ease-out"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-300 text-sm">
                    No photo available
                  </div>
                )}
                <div className="absolute top-4 left-4 bg-[#E63946] text-white px-3 py-1 rounded text-[10px] uppercase tracking-[0.2em] font-semibold">
                  New · {l.status}
                </div>
                <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur px-3 py-1.5 rounded font-display text-lg font-semibold">
                  {fmt(l.price)}
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display text-xl leading-tight truncate">
                      {l.address}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {l.city}
                      {l.state ? `, ${l.state}` : ""} {l.zip || ""}
                    </div>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-gray-400 group-hover:text-[#E63946] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0" />
                </div>

                <div className="mt-5 pt-5 border-t border-gray-100 flex items-center gap-5 text-xs text-[#333]">
                  <span className="flex items-center gap-1.5">
                    <Bed className="w-3.5 h-3.5 text-gray-400" strokeWidth={1.75} />
                    {l.beds ?? "—"} bd
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Bath className="w-3.5 h-3.5 text-gray-400" strokeWidth={1.75} />
                    {l.baths ?? "—"} ba
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Ruler className="w-3.5 h-3.5 text-gray-400" strokeWidth={1.75} />
                    {l.sqft ? Number(l.sqft).toLocaleString() : "—"} sqft
                  </span>
                </div>

                {l.list_office && (
                  <div className="mt-4 text-[10px] uppercase tracking-[0.15em] text-gray-400 truncate">
                    {l.list_office} · MLS #{l.listing_key}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
            IDX data © Houston Association of REALTORS® · Delivered via CoreLogic Trestle
          </div>
          <Link
            to="/mls-listings?min_price=750000"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-white hover:text-[#E63946] transition-colors"
            data-testid="mls-carousel-viewall"
          >
            View all luxury listings <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
